import React, { useEffect, useMemo, useState } from 'react';
import { Button, Form, Input, message, Typography } from 'antd';
import { Link, useNavigate } from 'react-router-dom';
import { confirmPasswordReset, requestPasswordReset } from '../../utils/cognitoAuth';
import { evaluatePasswordPolicy } from '../../utils/passwordPolicy';
import PasswordPolicyChecklist from './PasswordPolicyChecklist';

const { Paragraph } = Typography;

const RESEND_COOLDOWN_SECONDS = 30;

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('request');
  const [username, setUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [cooldownEndsAt, setCooldownEndsAt] = useState(0);
  const [nowTs, setNowTs] = useState(Date.now());
  const forgotPasswordPolicy = useMemo(() => evaluatePasswordPolicy(newPassword), [newPassword]);

  useEffect(() => {
    if (cooldownEndsAt <= Date.now()) {
      return undefined;
    }
    const intervalId = window.setInterval(() => setNowTs(Date.now()), 1000);
    return () => window.clearInterval(intervalId);
  }, [cooldownEndsAt]);

  const secondsToResend = useMemo(() => {
    const remaining = Math.ceil((cooldownEndsAt - nowTs) / 1000);
    return remaining > 0 ? remaining : 0;
  }, [cooldownEndsAt, nowTs]);

  const startCooldown = () => {
    setCooldownEndsAt(Date.now() + RESEND_COOLDOWN_SECONDS * 1000);
    setNowTs(Date.now());
  };

  const handleRequest = async ({ user }) => {
    setLoading(true);
    try {
      const requestedUser = user.trim();
      await requestPasswordReset({ username: requestedUser });
      setUsername(requestedUser);
      setStep('confirm');
      startCooldown();
      message.success('Se a conta existir, um codigo de recuperacao foi enviado.');
    } catch (error) {
      message.error(error?.message || 'Nao foi possivel iniciar a recuperacao de senha.');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async ({ code, newPassword, confirmPassword }) => {
    if (newPassword !== confirmPassword) {
      message.error('As senhas nao conferem.');
      return;
    }

    setLoading(true);
    try {
      await confirmPasswordReset({
        username,
        confirmationCode: code,
        newPassword,
      });
      message.success('Senha alterada com sucesso. Faca login novamente.');
      navigate('/login');
    } catch (error) {
      message.error(error?.message || 'Nao foi possivel confirmar a recuperacao de senha.');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!username || secondsToResend > 0) {
      return;
    }
    setLoading(true);
    try {
      await requestPasswordReset({ username });
      startCooldown();
      message.success('Codigo reenviado. Verifique sua caixa de entrada.');
    } catch (error) {
      message.error(error?.message || 'Nao foi possivel reenviar o codigo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <h2 className="login-title">Recuperar senha</h2>

      {step === 'request' ? (
        <Form onFinish={handleRequest} className="login-form" layout="vertical">
          <Form.Item
            name="user"
            label="Email ou usuario"
            rules={[{ required: true, message: 'Informe seu email/usuario.' }]}
            className="login-form-item"
          >
            <Input placeholder="seu.email@dominio.com" autoComplete="username" />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading} className="login-button">
              Enviar codigo
            </Button>
          </Form.Item>
          <Paragraph>
            <Link to="/login">Voltar para login</Link>
          </Paragraph>
        </Form>
      ) : (
        <Form onFinish={handleConfirm} className="login-form" layout="vertical">
          <Paragraph>
            Codigo enviado para <strong>{username}</strong>. Informe o codigo e a nova senha.
          </Paragraph>

          <Form.Item
            name="code"
            label="Codigo"
            rules={[{ required: true, message: 'Informe o codigo recebido.' }]}
            className="login-form-item"
          >
            <Input placeholder="123456" autoComplete="one-time-code" />
          </Form.Item>

          <Form.Item
            name="newPassword"
            label="Nova senha"
            rules={[
              { required: true, message: 'Informe a nova senha.' },
              {
                validator: (_, value) => {
                  const policy = evaluatePasswordPolicy(value);
                  if (policy.isValid) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('A senha nao atende a politica.'));
                },
              },
            ]}
            className="login-form-item"
          >
            <Input.Password
              placeholder="Nova senha"
              autoComplete="new-password"
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
            />
          </Form.Item>
          <PasswordPolicyChecklist password={newPassword} />

          <Form.Item
            name="confirmPassword"
            label="Confirmar nova senha"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: 'Confirme a nova senha.' },
              ({ getFieldValue }) => ({
                validator: (_, value) => {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('As senhas nao conferem.'));
                },
              }),
            ]}
            className="login-form-item"
          >
            <Input.Password placeholder="Confirmar nova senha" autoComplete="new-password" />
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              disabled={!forgotPasswordPolicy.isValid}
              className="login-button"
            >
              Confirmar nova senha
            </Button>
          </Form.Item>

          <Paragraph>
            <Button type="link" onClick={handleResend} disabled={secondsToResend > 0 || loading}>
              {secondsToResend > 0 ? `Reenviar codigo em ${secondsToResend}s` : 'Reenviar codigo'}
            </Button>
          </Paragraph>
        </Form>
      )}
    </div>
  );
};

export default ForgotPassword;
